import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import "./Navigation.css";
import { getUserDetails } from "../../Utils/functions";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { signOut as amplifySignOut } from "aws-amplify/auth";
import { navigationItems } from "./navigationConfig";
import UserButton from "../UserButton/UserButton";
import useAccessCheck from "../../hooks/useAccessCheck";

const Navigation = ({ children }) => {
  const navigate = useNavigate();
  const [isLitReviewOpen, setIsLitReviewOpen] = useState(false);
  const [isMobileLitReviewOpen, setIsMobileLitReviewOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isMobileProfileOpen, setIsMobileProfileOpen] = useState(false);
  const [isMedRPTAdmin, setIsMedRPTAdmin] = useState(false);
  const [isEnterpriseAdmin, setIsEnterpriseAdmin] = useState(false);
  const [enterpriseId, setEnterpriseId] = useState(null);
  const location = useLocation();
  const { id } = useParams();
  const { user } = useAuthenticator((context) => [context.user]);
  const { isAdvanced, isEnterpriseActive, loading: accessLoading } = useAccessCheck();
  const hasLitReviewAccess = Boolean(user && (isEnterpriseActive || isAdvanced));

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (user && user.userId) {
        try {
          const userDetails = await getUserDetails();
          setIsMedRPTAdmin(userDetails?.user?.isMedRPTAdmin || false);
          setIsEnterpriseAdmin(
            userDetails?.user?.EnterpriseUserRole === "ADMIN" || false
          );
          // Extract enterpriseId from userDetails
          const userEnterpriseId =
            userDetails?.user?.Enterprise?.id ||
            userDetails?.user?.enterpriseId;
          setEnterpriseId(userEnterpriseId || null);
        } catch (error) {
          console.error("Error checking MedRPT admin status:", error);
          setIsMedRPTAdmin(false);
          setIsEnterpriseAdmin(false);
          setEnterpriseId(null);
        }
      } else {
        setIsMedRPTAdmin(false);
        setIsEnterpriseAdmin(false);
        setEnterpriseId(null);
      }
    };
    checkAdminStatus();
  }, [user]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.classList.add("mobile-menu-open");
    } else {
      document.body.classList.remove("mobile-menu-open");
    }
  }, [isMobileMenuOpen]);

  const goToReportsPage = ({ isMobile = false, literatureReview = false } = {}) => {
    navigate(literatureReview ? "/reports?type=literature-review" : "/reports");
    if (isMobile) setIsMobileMenuOpen(false);
  };

  const toggleLitReview = () => {
    setIsLitReviewOpen(!isLitReviewOpen);
  };

  const toggleMobileLitReview = () => {
    setIsMobileLitReviewOpen(!isMobileLitReviewOpen);
  };

  const goToScreeningPage = (isMobile = false) => {
    navigate("/screening");
    if (isMobile) {
      setIsMobileMenuOpen(false);
    }
  };

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const toggleProfile = () => {
    setIsProfileOpen(!isProfileOpen);
  };

  const toggleMobileProfile = () => {
    setIsMobileProfileOpen(!isMobileProfileOpen);
  };

  const handleSignOut = async () => {
    try {
      await amplifySignOut();
    } catch (error) {
      console.error("Error signing out:", error);
    }
    window.location.assign("/login");
  };

  const getFirstTwoLetters = (email) => {
    return email ? email.slice(0, 2).toUpperCase() : "";
  };

  const renderCommonNavItems = () => {
    const items = [
      { id: "home", path: "/" },
      { id: "about", path: "/about" },
      { id: "disclaimer", path: "/disclaimer" },
      { id: "help", path: "/help" },
      { id: "privacy", path: "/privacy" },
    ];
    return items.map((item) => {
      const navItem = navigationItems.find((nav) => nav.id === item.id);
      if (!navItem) return null;

      return (
        <button
          key={item.id}
          className="nav-button"
          onClick={() => navigate(item.path)}
        >
          {navItem.svg}
          <span className="nav-content">{navItem.text}</span>
        </button>
      );
    });
  };

  const renderAuthButton = () => {
    if (user) {
      const logOutItem = navigationItems.find((item) => item.id === "logout");
      return (
        <button className="nav-button" onClick={handleSignOut}>
          {logOutItem?.svg}
          <span className="nav-content">{logOutItem?.text}</span>
        </button>
      );
    } else {
      const loginItem = navigationItems.find((item) => item.id === "login");
      return (
        <button className="nav-button" onClick={() => navigate("/login")}>
          {loginItem?.svg}
          <span className="nav-content">{loginItem?.text}</span>
        </button>
      );
    }
  };

  const renderScheduleJobButton = () => {
    if (user) {
      const scheduleRunsItem = navigationItems.find(
        (item) => item.id === "scheduleRuns"
      );
      return (
        <button
          className="nav-button"
          onClick={() => navigate("/scheduled-runs")}
        >
          {scheduleRunsItem?.svg}
          <span className="nav-content">{scheduleRunsItem?.text}</span>
        </button>
      );
    }
  };

  const renderEnterpriseButton = () => {
    if (isMedRPTAdmin) {
      const enterpriseItem = navigationItems.find(
        (item) => item.id === "enterprise"
      );
      return (
        <button className="nav-button" onClick={() => navigate("/enterprise")}>
          {enterpriseItem?.svg}
          <span className="nav-content">{enterpriseItem?.text}</span>
        </button>
      );
    } else if (isEnterpriseAdmin && enterpriseId) {
      const enterpriseItem = navigationItems.find(
        (item) => item.id === "enterprise"
      );
      return (
        <button
          className="nav-button"
          onClick={() => navigate(`/enterprise/${enterpriseId}`)}
        >
          {enterpriseItem?.svg}
          <span className="nav-content">{enterpriseItem?.text}</span>
        </button>
      );
    }
    return null;
  };

  const renderReportButton = (isMobile = false) => {
    const reportsItem = navigationItems.find((item) => item.id === "reports");
    if (!reportsItem) return null;

    return (
      <button
        className="nav-button"
        onClick={() => goToReportsPage({ isMobile })}
      >
        {reportsItem.svg}
        <span className="nav-content">{reportsItem.text}</span>
      </button>
    );
  };

  const renderLitReviewButton = (isMobile = false) => {
    const screeningItem = navigationItems.find((nav) => nav.id === "screening");
    if (!screeningItem) return null;

    const isOpen = isMobile ? isMobileLitReviewOpen : isLitReviewOpen;
    const handleChevronClick = (e) => {
      e.stopPropagation();
      if (isMobile) {
        toggleMobileLitReview();
      } else {
        toggleLitReview();
      }
    };

    return (
      <button
        type="button"
        className="nav-button"
        onClick={() => goToScreeningPage(isMobile)}
      >
        {screeningItem.svg}
        <span className="nav-content">{screeningItem.text}</span>
        <svg
          className={`chevron-icon ${isOpen ? "open" : ""}`}
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
          onClick={handleChevronClick}
          role="button"
          aria-label={isOpen ? "Collapse literature reviews" : "Expand literature reviews"}
          aria-expanded={isOpen}
        >
          <path
            d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"
            fill="currentColor"
          />
        </svg>
      </button>
    );
  };

  return (
    <div className="black-container">
      <aside className={`sidebar ${isSidebarCollapsed ? "collapsed" : ""}`}>
        <div className="logo-header">
          <div
            className="logo-container"
            onClick={isSidebarCollapsed ? toggleSidebar : undefined}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="29"
              viewBox="0 0 24 29"
              fill="none"
            >
              <path
                d="M17.0231 6.66178L19.539 8.07667L13.1694 12.1866V15.2859L23.9301 9.04685V7.10849L11.8836 0L0 7.27234V10.8248L11.9633 3.71635L14.2922 5.04476L0 13.8036V17.3072L17.0231 6.66178Z"
                fill="white"
              />
              <path
                d="M3.90724 21.0883L10.7603 16.6415L10.7603 13.5422L-0.00041507 20.455L-0.000415239 22.3934L12.0461 28.8281L23.9297 21.5558L23.9297 18.0033L23.9297 12.5316L21.5597 13.9128L21.5597 19.2692L11.9664 25.1118L3.90724 21.0883Z"
                fill="white"
              />
            </svg>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="143"
              height="36"
              viewBox="20 0 143 36"
              fill="none"
            >
              <path
                d="M69.207 8.74219H75.6133C76.8938 8.74219 78.0708 8.9502 79.1445 9.36623C80.2142 9.78226 81.1368 10.3623 81.9122 11.1023C82.6877 11.8424 83.2841 12.7105 83.7057 13.7105C84.1272 14.7106 84.3379 15.7907 84.3379 16.9547V17.0027C84.3379 18.1668 84.1272 19.2549 83.7057 20.259C83.2841 21.267 82.6837 22.1391 81.9122 22.8791C81.1368 23.6192 80.2142 24.1992 79.1445 24.6273C78.0748 25.0513 76.8978 25.2633 75.6133 25.2633H69.207V12.1224V8.74219ZM75.6133 21.9831C76.349 21.9831 77.021 21.8671 77.6294 21.6311C78.2379 21.395 78.7588 21.055 79.1883 20.615C79.6177 20.175 79.9557 19.6549 80.1983 19.0589C80.4409 18.4588 80.5602 17.7908 80.5602 17.0507V17.0027C80.5602 16.2787 80.4409 15.6107 80.1983 14.9986C79.9557 14.3866 79.6177 13.8585 79.1883 13.4185C78.7588 12.9785 78.2379 12.6344 77.6294 12.3904C77.021 12.1464 76.345 12.0264 75.6133 12.0264H69.2388L69.211 21.7871V21.9871H75.6133V21.9831Z"
                fill="white"
              />
              <path
                d="M33.125 8.74219H37.0181L41.3367 15.7267L45.6552 8.74219H49.5483V25.2633H45.9813V14.4786L41.3605 21.5351H41.2651L36.692 14.5506V25.2673H33.125V8.74219Z"
                fill="white"
              />
              <path
                d="M87.5586 8.74219H95.0664C97.1461 8.74219 98.7408 9.30223 99.8542 10.4183C100.793 11.3624 101.262 12.6224 101.262 14.1946V14.2426C101.262 15.5786 100.936 16.6707 100.288 17.5108C99.6395 18.3508 98.7885 18.9709 97.7426 19.3629L101.755 25.2633H97.5319L94.0126 19.9749H93.9649H91.1733V25.2633H87.5586V8.74219ZM94.8318 16.7667C95.7225 16.7667 96.4065 16.5547 96.8837 16.1307C97.3609 15.7067 97.5995 15.1386 97.5995 14.4306V14.3826C97.5995 13.5945 97.349 13.0065 96.8479 12.6144C96.3469 12.2224 95.6509 12.0264 94.7602 12.0264H91.1693V16.7667H94.8318Z"
                fill="#4FC04F"
              />
              <path
                d="M104.52 8.74219H111.228C112.214 8.74219 113.093 8.8782 113.868 9.15422C114.644 9.43023 115.3 9.81826 115.841 10.3223C116.382 10.8263 116.795 11.4264 117.086 12.1264C117.376 12.8265 117.519 13.6025 117.519 14.4506V14.4986C117.519 15.4586 117.348 16.2987 117.002 17.0227C116.656 17.7468 116.183 18.3508 115.582 18.8389C114.982 19.3269 114.278 19.6909 113.471 19.9349C112.664 20.179 111.801 20.303 110.878 20.303H108.134V25.2593H104.52V8.74219ZM110.993 17.0747C111.9 17.0747 112.604 16.8347 113.105 16.3547C113.606 15.8747 113.857 15.2826 113.857 14.5746V14.5266C113.857 13.7105 113.594 13.0865 113.069 12.6625C112.544 12.2384 111.828 12.0264 110.922 12.0264H108.13V17.0787H110.993V17.0747Z"
                fill="#4FC04F"
              />
              <path
                d="M124.018 12.0944H119.02V8.74219H132.627V12.0944H127.629V25.2633H124.014V12.0944H124.018Z"
                fill="#4FC04F"
              />
              <path
                d="M53.4922 8.74219H65.8554V11.9744H57.0592V15.3266H76.3337V18.5588H57.0592V22.0271H65.9747V25.2593H53.4922V8.74219Z"
                fill="white"
              />
            </svg>
          </div>
          {!isSidebarCollapsed && (
            <button className="collapse-button" onClick={toggleSidebar}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="13"
                height="13"
                viewBox="0 0 13 13"
                fill="none"
              >
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M5.74722 11.7456C6.01054 11.4065 6.01054 10.8567 5.74722 10.5175L3.30213 7.36842L2.60067 6.58184C2.60067 6.58184 2.6857 6.40561 2.57332 6.5L2.60067 6.49151L3.30213 5.63158L5.74722 2.48249C6.01054 2.14335 6.01054 1.59349 5.74722 1.25436C5.4839 0.915216 5.05697 0.915216 4.79365 1.25436L1.19749 5.88593C0.93417 6.22507 0.93417 6.77493 1.19749 7.11407L4.79365 11.7456C5.05697 12.0848 5.4839 12.0848 5.74722 11.7456Z"
                  fill="#FAFAF9"
                />
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M12.49 11.7456C12.7533 11.4065 12.7533 10.8567 12.49 10.5175L10.0449 7.36842L9.34346 6.58184C9.34346 6.58184 9.42849 6.40561 9.31611 6.5L9.34346 6.49151L10.0449 5.63158L12.49 2.48249C12.7533 2.14335 12.7533 1.59349 12.49 1.25436C12.2267 0.915217 11.7998 0.915216 11.5364 1.25436L7.94028 5.88593C7.67696 6.22507 7.67696 6.77493 7.94028 7.11407L11.5364 11.7456C11.7998 12.0848 12.2267 12.0848 12.49 11.7456Z"
                  fill="#FAFAF9"
                />
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M5.74722 11.7456C6.01054 11.4065 6.01054 10.8567 5.74722 10.5175L3.30213 7.36842L2.60067 6.58184C2.60067 6.58184 2.6857 6.40561 2.57332 6.5L2.60067 6.49151L3.30213 5.63158L5.74722 2.48249C6.01054 2.14335 6.01054 1.59349 5.74722 1.25436C5.4839 0.915216 5.05697 0.915216 4.79365 1.25436L1.19749 5.88593C0.93417 6.22507 0.93417 6.77493 1.19749 7.11407L4.79365 11.7456C5.05697 12.0848 5.4839 12.0848 5.74722 11.7456Z"
                  stroke="white"
                  stroke-width="0.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M12.49 11.7456C12.7533 11.4065 12.7533 10.8567 12.49 10.5175L10.0449 7.36842L9.34346 6.58184C9.34346 6.58184 9.42849 6.40561 9.31611 6.5L9.34346 6.49151L10.0449 5.63158L12.49 2.48249C12.7533 2.14335 12.7533 1.59349 12.49 1.25436C12.2267 0.915217 11.7998 0.915216 11.5364 1.25436L7.94028 5.88593C7.67696 6.22507 7.67696 6.77493 7.94028 7.11407L11.5364 11.7456C11.7998 12.0848 12.2267 12.0848 12.49 11.7456Z"
                  stroke="white"
                  stroke-width="0.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          )}
        </div>
        <div className="sidebar-navigation">
          {user && (
            <>
              {renderReportButton(false)}
              {!accessLoading && renderLitReviewButton(false)}
              {hasLitReviewAccess && isLitReviewOpen && (
                <ul className="completed-questions">
                  <li className="questions-list">
                    <button className="question" onClick={() => navigate("/screening")}>
                      New Search
                    </button>
                  </li>
                  <li className="questions-list">
                    <button
                      className="question"
                      onClick={() => goToReportsPage({ literatureReview: true })}
                    >
                      Reports
                    </button>
                  </li>
                </ul>
              )}
            </>
          )}
          {renderCommonNavItems()}
          {renderScheduleJobButton()}
          {renderEnterpriseButton()}
          {user && (
            <>
              <button className="nav-button" onClick={toggleProfile}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="25"
                  height="25"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    d="M9.5498 1.74609C14.2765 1.74609 18.0996 5.55782 18.0996 10.249C18.0995 14.9401 14.2764 18.751 9.5498 18.751C4.82326 18.7509 1.00015 14.94 1 10.249C1 5.55789 4.82317 1.7462 9.5498 1.74609Z"
                    stroke="white"
                    stroke-width="1.2"
                  />
                  <text
                    x="10"
                    y="13"
                    textAnchor="middle"
                    fill="white"
                    fontSize="9"
                    fontWeight="bold"
                  >
                    {" "}
                    {getFirstTwoLetters(user?.signInDetails?.loginId)}{" "}
                  </text>
                </svg>
                <span className="nav-content">Profile</span>
                <svg
                  className={`chevron-icon ${isProfileOpen ? "open" : ""}`}
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"
                    fill="currentColor"
                  />
                </svg>
              </button>

              {isProfileOpen && (
                <UserButton
                  isProfileOpen={isProfileOpen}
                  profileMenu="profile-menu"
                />
              )}
            </>
          )}
          {renderAuthButton()}
        </div>
      </aside>

      <div className="main-content-wrapper">
        <div className="mobile-top-bar">
          <button
            className={`mobile-menu-button ${isMobileMenuOpen ? "collapsed" : ""
              }`}
            onClick={toggleMobileMenu}
          >
            {isMobileMenuOpen ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="28"
                height="28"
                viewBox="0 0 28 28"
                fill="none"
                onClick={(e) => {
                  e.stopPropagation();
                  closeMobileMenu();
                }}
              >
                <path
                  d="M3.24635 3L25 24.8417M24.7537 3.15834L3 25"
                  stroke="white"
                  stroke-width="4.5"
                  stroke-linecap="round"
                />
              </svg>
            ) : (
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3 12H21M3 6H21M3 18H21"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </button>
          <div className="mobile-logo-container">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="29"
              viewBox="0 0 24 29"
              fill="none"
            >
              <path
                d="M17.0231 6.66178L19.539 8.07667L13.1694 12.1866V15.2859L23.9301 9.04685V7.10849L11.8836 0L0 7.27234V10.8248L11.9633 3.71635L14.2922 5.04476L0 13.8036V17.3072L17.0231 6.66178Z"
                fill="white"
              />
              <path
                d="M3.90724 21.0883L10.7603 16.6415L10.7603 13.5422L-0.00041507 20.455L-0.000415239 22.3934L12.0461 28.8281L23.9297 21.5558L23.9297 18.0033L23.9297 12.5316L21.5597 13.9128L21.5597 19.2692L11.9664 25.1118L3.90724 21.0883Z"
                fill="white"
              />
            </svg>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="122"
              height="31"
              viewBox="20 0 122 31"
              fill="none"
            >
              <path
                d="M59.2637 7.41797H64.6993C65.7858 7.41797 66.7845 7.59447 67.6955 7.94746C68.6031 8.30045 69.3859 8.79261 70.0439 9.42053C70.7018 10.0485 71.2079 10.785 71.5656 11.6335C71.9232 12.4821 72.1021 13.3985 72.1021 14.3862V14.4269C72.1021 15.4146 71.9232 16.3379 71.5656 17.1898C71.2079 18.0451 70.6984 18.7851 70.0439 19.413C69.3859 20.0409 68.6031 20.5331 67.6955 20.8962C66.7879 21.256 65.7891 21.4359 64.6993 21.4359H59.2637V10.286V7.41797ZM64.6993 18.6527C65.3235 18.6527 65.8937 18.5543 66.41 18.354C66.9262 18.1537 67.3682 17.8652 67.7326 17.4919C68.097 17.1185 68.3838 16.6773 68.5896 16.1715C68.7955 15.6624 68.8967 15.0956 68.8967 14.4677V14.4269C68.8967 13.8126 68.7955 13.2458 68.5896 12.7265C68.3838 12.2072 68.097 11.7591 67.7326 11.3858C67.3682 11.0124 66.9262 10.7205 66.41 10.5135C65.8937 10.3064 65.3201 10.2046 64.6993 10.2046H59.2907L59.267 18.4864V18.6561H64.6993V18.6527Z"
                fill="white"
              />
              <path
                d="M28.6484 7.41797H31.9517L35.6159 13.3442L39.2802 7.41797H42.5834V21.4359H39.5568V12.2852L35.6362 18.2725H35.5552L31.675 12.3463V21.4393H28.6484V7.41797Z"
                fill="white"
              />
              <path
                d="M74.834 7.41797H81.2042C82.9689 7.41797 84.3219 7.89315 85.2666 8.84013C86.0629 9.64115 86.4611 10.7103 86.4611 12.0442V12.085C86.4611 13.2186 86.1844 14.1452 85.6344 14.858C85.0844 15.5708 84.3624 16.0969 83.475 16.4295L86.8795 21.4359H83.2962L80.3101 16.9488H80.2696H77.901V21.4359H74.834V7.41797ZM81.0052 14.2267C81.761 14.2267 82.3413 14.0468 82.7462 13.687C83.1511 13.3272 83.3535 12.8453 83.3535 12.2445V12.2038C83.3535 11.5351 83.141 11.0362 82.7158 10.7035C82.2907 10.3709 81.7002 10.2046 80.9444 10.2046H77.8977V14.2267H81.0052Z"
                fill="#4FC04F"
              />
              <path
                d="M89.2266 7.41797H94.9186C95.7554 7.41797 96.5011 7.53337 97.159 7.76757C97.817 8.00177 98.3737 8.331 98.8326 8.75867C99.2914 9.18633 99.6424 9.69546 99.8887 10.2894C100.135 10.8834 100.256 11.5419 100.256 12.2615V12.3022C100.256 13.1168 100.111 13.8296 99.8178 14.4439C99.5243 15.0583 99.1227 15.5708 98.6133 15.9849C98.1038 16.399 97.5066 16.7078 96.8216 16.9149C96.1367 17.1219 95.4045 17.2271 94.6217 17.2271H92.2936V21.4325H89.2266V7.41797ZM94.7196 14.488C95.4889 14.488 96.0861 14.2844 96.5112 13.8771C96.9363 13.4698 97.1489 12.9674 97.1489 12.3667V12.3259C97.1489 11.6335 96.9262 11.104 96.4808 10.7443C96.0355 10.3845 95.4281 10.2046 94.6588 10.2046H92.2902V14.4914H94.7196V14.488Z"
                fill="#4FC04F"
              />
              <path
                d="M105.771 10.2623H101.529V7.41797H113.075V10.2623H108.834V21.4359H105.767V10.2623H105.771Z"
                fill="#4FC04F"
              />
              <path
                d="M45.9297 7.41797H56.4197V10.1605H48.9562V13.0048H65.3104V15.7473H48.9562V18.69H56.5209V21.4325H45.9297V7.41797Z"
                fill="white"
              />
            </svg>
          </div>
        </div>

        {isMobileMenuOpen && (
          <div className="mobile-menu-content">
            {user && (
              <>
                {renderReportButton(true)}
                {!accessLoading && renderLitReviewButton(true)}
                {hasLitReviewAccess && isMobileLitReviewOpen && (
                  <ul className="completed-questions">
                    <li className="questions-list">
                      <button
                        className="question"
                        onClick={() => {
                          navigate("/screening");
                          setIsMobileMenuOpen(false);
                        }}
                      >
                        New Search
                      </button>
                    </li>
                    <li className="questions-list">
                      <button
                        className="question"
                        onClick={() => goToReportsPage({ isMobile: true, literatureReview: true })}
                      >
                        Reports
                      </button>
                    </li>
                  </ul>
                )}
              </>
            )}
            {renderCommonNavItems()}
            {renderScheduleJobButton()}
            {renderEnterpriseButton()}
            {user && (
              <>
                <button className="nav-button" onClick={toggleMobileProfile}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="25"
                    height="25"
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <path
                      d="M9.5498 1.74609C14.2765 1.74609 18.0996 5.55782 18.0996 10.249C18.0995 14.9401 14.2764 18.751 9.5498 18.751C4.82326 18.7509 1.00015 14.94 1 10.249C1 5.55789 4.82317 1.7462 9.5498 1.74609Z"
                      stroke="white"
                      stroke-width="1.2"
                    />
                    <text
                      x="10"
                      y="13"
                      textAnchor="middle"
                      fill="white"
                      fontSize="9"
                      fontWeight="bold"
                    >
                      {" "}
                      {getFirstTwoLetters(user?.signInDetails?.loginId)}{" "}
                    </text>
                  </svg>
                  <span className="nav-content">Profile</span>
                  <svg
                    className={`chevron-icon ${isMobileProfileOpen ? "open" : ""
                      }`}
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"
                      fill="currentColor"
                    />
                  </svg>
                </button>

                {isMobileProfileOpen && (
                  <UserButton
                    isProfileOpen={isMobileProfileOpen}
                    profileMenu="profile-menu"
                  />
                )}
              </>
            )}
            {renderAuthButton()}
          </div>
        )}
        <main className="main-content">{children}</main>
      </div>
    </div>
  );
};

export default Navigation;
